"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { AlertTriangle, ArrowLeft, RefreshCw, HelpCircle, Copy, Check } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import LogoHeader from "./functions/header/logo-header"
import { logger } from "@/lib/logger"

interface ErrorScreenProps {
  message: string
  onBackToHome: () => void
  onNavigateToHome: () => void
}

export default function ErrorScreen({ message, onBackToHome, onNavigateToHome }: ErrorScreenProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyLogs = async () => {
    logger.error("Error screen displayed", { errorMessage: message });
    const success = await logger.copyLogsToClipboard();
    if (success) {
      setCopied(true);
      toast.success("Logi skopiowane do schowka!", {
        description: "Możesz je teraz wkleić do wiadomości.",
        duration: 3000,
      });
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Nie udało się skopiować logów");
    }
  };
  // Clean up message if it has a prefix like "recognition:"
  const cleanMessage = message.startsWith("recognition:") 
    ? message.replace("recognition:", "").trim()
    : message;
  
  const getTroubleshootingTips = (errorMessage: string) => {
    if (errorMessage.includes("file format") || errorMessage.includes("format pliku")) {
      return [
        "Upewnij się, że plik to obraz (JPG, PNG) lub PDF",
        "Spróbuj przekonwertować plik na obsługiwany format",
        "Sprawdź, czy plik nie jest uszkodzony",
      ]
    }

    if (errorMessage.includes("size")) {
      return [
        "Skompresuj obraz lub PDF, aby zmniejszyć rozmiar pliku",
        "Spróbuj przesłać mniejszy plik (poniżej 10MB)",
        "Użyj narzędzi online do optymalizacji pliku",
      ]
    }

    if (errorMessage.includes("camera")) {
      return [
        "Zezwól na dostęp do aparatu w ustawieniach przeglądarki",
        "Spróbuj odświeżyć stronę i udzielić pozwolenia",
        "Użyj opcji przesyłania pliku zamiast tego",
      ]
    }

    // Checked before the recognition branch: a timeout is not a bad-photo problem,
    // and the advice that actually helps is to leave the screen alone.
    if (errorMessage.includes("zbyt długo")) {
      return [
        "Zostaw ten ekran otwarty do końca przetwarzania",
        "Nie blokuj telefonu i nie przechodź do innej aplikacji",
        "Sprawdź połączenie internetowe",
        "W przypadku dużego dokumentu spróbuj podzielić go na mniejsze części",
      ]
    }

    if (errorMessage.includes("recognition") || errorMessage.includes("rozpoznać") || errorMessage.includes("przetworzyć")) {
      return [
        "Upewnij się, że tekst jest wyraźny i dobrze oświetlony",
        "Spróbuj zrobić nowe zdjęcie z lepszym oświetleniem",
        "Upewnij się, że tekst nie jest rozmazany lub zniekształcony",
        "Używaj obrazów o wysokim kontraście (ciemny tekst na jasnym tle)",
        "Spróbuj zeskanować dokument zamiast fotografować",
      ]
    }
    
    if (errorMessage.includes("połączenie") || errorMessage.includes("sieć") || errorMessage.includes("serwer")) {
      return [
        "Sprawdź połączenie internetowe",
        "Spróbuj odświeżyć stronę",
        "Poczekaj chwilę i spróbuj ponownie",
        "Aplikacja zadziała offline z ograniczoną jakością rozpoznawania",
      ]
    }

    return [
      "Sprawdź połączenie internetowe",
      "Spróbuj odświeżyć stronę",
      "Upewnij się, że plik nie jest uszkodzony",
      "Skontaktuj się z pomocą techniczną, jeśli problem się powtarza",
    ]
  }

  const troubleshootingTips = getTroubleshootingTips(message)

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex flex-col">
      <LogoHeader 
        onLogoClick={onNavigateToHome}
        onBackClick={onBackToHome}
        showBackButton={true}
      />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg space-y-6">
          {/* Error Icon and Message */}
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>

              <div className="space-y-2">
                <h2 className="text-xl font-bold text-red-900">Ups! Coś poszło nie tak</h2>
                <p className="text-red-700">{cleanMessage}</p>
              </div>
            </CardContent>
          </Card>

          {/* Troubleshooting Tips */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-text-main flex items-center">
                <HelpCircle className="h-5 w-5 mr-2 text-blue-600" />
                Wypróbuj te rozwiązania:
              </h3>

              <ul className="space-y-3">
                {troubleshootingTips.map((tip, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-text-secondary">{tip}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button onClick={onBackToHome} className="w-full bg-[rgba(85,54,109,1)] hover:bg-[rgba(85,54,109,0.9)] text-white" size="lg">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Spróbuj ponownie
            </Button>

            <Button variant="outline" onClick={() => window.location.reload()} className="w-full" size="lg">
              <RefreshCw className="h-4 w-4 mr-2" />
              Odśwież stronę
            </Button>
          </div>

          {/* Help Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-800 mb-2">
              <strong>Nadal masz problemy?</strong>
            </p>
            <p className="text-sm text-blue-700">
              Nasz zespół wsparcia jest tutaj, aby pomóc. Skopiuj logi diagnostyczne i wyślij je do nas.
            </p>
            <div className="flex flex-col items-center gap-2 mt-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleCopyLogs}
                className="text-[rgba(85,54,109,1)] border-[rgba(85,54,109,0.3)] hover:bg-[rgba(85,54,109,0.1)]"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Skopiowano!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Skopiuj logi diagnostyczne
                  </>
                )}
              </Button>
              <Button variant="link" className="text-[rgba(85,54,109,1)] p-0 h-auto">
                Skontaktuj się z pomocą
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
